import { createI18n } from 'vue-i18n';
import { i18nCommon, loadCommonLocale } from '@r7e/shared';
import { consumerEn } from './locales/consumer.en';

// Cache for loaded app-specific locales
const loadedAppLocales = {
  en: consumerEn,
};

// Lazy load app-specific locale function
async function loadAppLocale(locale) {
  // Return cached locale if already loaded
  if (loadedAppLocales[locale]) {
    return loadedAppLocales[locale];
  }

  // Load locale dynamically
  let localeModule;
  switch (locale) {
    case 'fr':
      localeModule = await import('./locales/consumer.fr');
      loadedAppLocales.fr = localeModule.consumerFr;
      return loadedAppLocales.fr;
    case 'de':
      localeModule = await import('./locales/consumer.de');
      loadedAppLocales.de = localeModule.consumerDe;
      return loadedAppLocales.de;
    case 'es':
      localeModule = await import('./locales/consumer.es');
      loadedAppLocales.es = localeModule.consumerEs;
      return loadedAppLocales.es;
    case 'it':
      localeModule = await import('./locales/consumer.it');
      loadedAppLocales.it = localeModule.consumerIt;
      return loadedAppLocales.it;
    case 'ja':
      localeModule = await import('./locales/consumer.ja');
      loadedAppLocales.ja = localeModule.consumerJa;
      return loadedAppLocales.ja;
    case 'zh':
      localeModule = await import('./locales/consumer.zh');
      loadedAppLocales.zh = localeModule.consumerZh;
      return loadedAppLocales.zh;
    case 'en':
      return loadedAppLocales.en;
    default:
      return loadedAppLocales.en; // Fallback to English
  }
}

// Initialize messages with only English loaded
const messages = {
  en: { ...i18nCommon.en || {}, ...consumerEn },
};

// Always initialize with 'en' - saved locale will be loaded in main.js
const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages,
});

// Function to load a locale on demand
export async function loadLocale(locale) {
  // If locale is already loaded, just switch to it
  if (i18n.global.availableLocales.includes(locale)) {
    i18n.global.locale.value = locale;
    return;
  }

  // Load both common and app-specific locales
  const [commonLocale, appLocale] = await Promise.all([
    loadCommonLocale(locale),
    loadAppLocale(locale),
  ]);

  // Merge and set the locale messages
  i18n.global.setLocaleMessage(locale, {
    ...commonLocale,
    ...appLocale,
  });

  // Switch to the new locale
  i18n.global.locale.value = locale;
}

export default i18n; 